/**
 * ClickHandler.js
 * -------------------------------
 * Handles contact button click processing and configuration logic.
 * Acts as a controller between user interaction (button click) and system behavior
 * (analytics tracking, modal presentation, fallback handling).
 *
 * Responsibilities:
 * - Validate and respond to contact button clicks
 * - Trigger analytics events using the shared ContactNamespace
 * - Extract and apply configuration settings from the clicked button
 * - Perform system health checks before attempting to open the modal
 * - Attempt recovery via reinitialization if the modal is not ready
 * - Invoke fallback behavior when modal handling fails
 * - Support manual triggering (e.g., programmatic call to `.processManualTrigger()`)
 * - Track critical click processing failures with essential error monitoring
 *
 * Features:
 * - Defensive error handling for all stages: tracking, config, modal opening
 * - Logs events and states using `DebugHelper`
 * - Reinitializes modal if it's partially broken but recoverable
 * - Creates and uses a virtual button when invoked manually
 * - Provides diagnostics and health stats via `.getStats()`
 * - Essential error tracking for critical click processing failures
 * - Basic debugging capabilities for troubleshooting production issues
 *
 * Error Handling:
 * - Uses ErrorHandler for essential click processing failure tracking
 * - Categorizes critical errors: 'dependency', 'modal-operation', 'manual-trigger'
 * - Tracks only the most important failures to avoid code bloat
 * - Provides basic error access methods for debugging
 * - Distinguishes between recoverable warnings and critical failures requiring fallback
 *
 * Key Methods:
 * - `processClick(button)`             : Main entry point for button click flow
 * - `trackClick(button)`               : Sends analytics event
 * - `applyButtonConfiguration(button)` : Reads and applies data attributes from button
 * - `handleModalOpening()`             : Coordinates modal readiness and opening logic
 * - `shouldReinitializeModal()`        : Determines whether recovery is necessary
 * - `reinitializeModal()`              : Tries to fix modal by calling `reinitializeModalSync()`
 * - `processManualTrigger()`           : Simulates a click event with a generated button
 * - `validateButton(button)`           : Ensures the element is a valid contact trigger
 * - `getStats()`                       : Exposes runtime health info and basic error data
 * - `getRecentErrors()`                : Returns recent critical errors for debugging
 * - `hasCriticalIssues()`              : Quick check for fatal click processing errors
 *
 * Limitations:
 * - Assumes `namespace` is pre-initialized and exposes expected methods
 * - Not SSR-safe (assumes `document`, `window`, and DOM are available)
 * - FallbackHandler uses injected HTML and may not be CSP-safe
 * - Error tracking is minimal to avoid code explosion (only critical failures)
 * - No detailed click analytics or performance monitoring
 */
/**
 * ClickHandler.js
 * -------------------------------
 * Enhanced with dynamic modal configuration support
 */

import DebugHelper from '../utils/DebugHelper.js';
import ModalHandler  from './ModalHandler.js';
import FallbackHandler  from './FallbackHandler.js';
import ErrorHandler  from '../../utils/ErrorHandler.js';

export default class ClickHandler {
    constructor(namespace) {
        this.namespace = namespace;
        this.logger = DebugHelper.createContext('ClickHandler');
        this.pendingModalConfig = null; // Store modal config for later use
        
        // Initialize minimal error handler
        this.errorHandler = new ErrorHandler({
            module: 'ClickHandler',
            maxErrors: 20
        });
    }

    /**
     * Process a contact button click
     * @param {HTMLElement} button The clicked button element
     */
    processClick(button) {
        this.logger.info('Contact button clicked - processing...');
        this.lastProcessed = new Date().toISOString();
        this.lastClickedButtonId = button?.id || '';
        
        if (!this.namespace) {
            const error = new Error('Contact namespace not available');
            this.errorHandler.handleFatalError('Critical dependency missing', error, {
                category: 'dependency',
                buttonId: button?.id || 'unknown'
            });
            FallbackHandler.handle({ trigger: 'missing-namespace' });
            return;
        }

        // Validate button before processing
        if (!this.validateButton(button)) {
            this.errorHandler.handleError('Invalid button element provided', new Error('Button validation failed'), {
                category: 'validation',
                buttonClass: button?.className || 'none'
            });
            FallbackHandler.handle({ trigger: 'invalid-button' });
            return;
        }

        // Track the click
        this.trackClick(button);

        // Apply button configuration
        this.applyButtonConfiguration(button);

        // Extract modal configuration if present
        const modalConfig = this.extractModalConfig(button);
        if (modalConfig) {
            // Store modal config for use when modal opens
            this.pendingModalConfig = modalConfig;
            this.logger.debug('Modal configuration extracted', { 
                modalType: modalConfig.modalType,
                hasWorkshopTitle: !!modalConfig.triggerData.workshopTitle
            });
        }

        // Check system health and open modal
        this.handleModalOpening(button);
    }

    /**
     * Extract modal configuration from button
     * @param {HTMLElement} button The clicked button
     * @returns {Object|null} Modal configuration or null
     */
    extractModalConfig(button) {
        // Redesign CTA triggers (data-cta) need no data-modal-type; data-mode
        // names their form mode, falling back to the page's default config.
        const modalType = button.dataset.modalType
            || (button.dataset.cta ? (button.dataset.mode || 'default') : null);
        if (!modalType) return null;

        return {
            modalType,
            triggerData: {
                workshopTitle: button.dataset.eventTitle || button.dataset.workshopTitle,
                workshopDate: button.dataset.eventDate || button.dataset.workshopDate,
                eventTitle: button.dataset.eventTitle || button.dataset.workshopTitle,
                eventDate: button.dataset.eventDate || button.dataset.workshopDate,
                facilitator: button.dataset.facilitator,
                pageSlug: button.dataset.pageSlug,
                buttonId: button.id || '',
                ...button.dataset
            }
        };
    }

    /**
     * Get the pending modal configuration
     * @returns {Object|null} Pending modal config or null
     */
    getPendingModalConfig() {
        return this.pendingModalConfig;
    }

    /**
     * Clear the pending modal configuration
     */
    clearPendingModalConfig() {
        this.pendingModalConfig = null;
    }

    /**
     * Track the button click in analytics
     * @param {HTMLElement} button The clicked button
     */
    trackClick(button) {
        try {
            this.namespace.trackClick(button);
            this.logger.debug('Button click tracked successfully');
        } catch (error) {
            this.errorHandler.handleError('Analytics tracking failed', error, {
                category: 'analytics',
                buttonId: button?.id || 'unknown'
            });
        }
    }

    /**
     * Apply configuration from the button
     * @param {HTMLElement} button The clicked button
     */
    applyButtonConfiguration(button) {
        try {
            const config = this.namespace.getButtonConfig(button);
            if (config) {
                DebugHelper.logConfig(config);
                this.namespace.applyConfig(config);
                this.logger.debug('Button configuration applied');
            } else {
                this.logger.debug('No specific configuration found for button');
            }
        } catch (error) {
            this.errorHandler.handleError('Configuration application failed', error, {
                category: 'configuration',
                buttonId: button?.id || 'unknown'
            });
        }
    }

    /**
     * Handle the modal opening process
     * @param {HTMLElement} button The clicked button for context
     */
    handleModalOpening(button) {
        const modalHandler = new ModalHandler(this.namespace);
        
        try {
            // Check system health
            const health = this.namespace.healthCheck();
            DebugHelper.logHealthCheck(health);

            // Handle modal opening based on health status
            if (this.shouldReinitializeModal(health)) {
                this.logger.warn('Modal exists but not ready, attempting reinitialize...');
                
                if (!this.reinitializeModal(button)) {
                    this.errorHandler.handleFatalError('Modal reinitialize failed after health check', new Error('Reinitialize returned false'), {
                        category: 'modal-operation',
                        action: 'reinitialize',
                        buttonId: button?.id || 'unknown'
                    });
                    FallbackHandler.handle({ trigger: 'reinitialize-failed' });
                    return;
                }
            } else if (!health.components.modal) {
                this.logger.info('Modal not yet created - will be lazy loaded on open');
            }

            // Attempt to open the modal
            if (!modalHandler.openModal()) {
                this.errorHandler.handleFatalError('Modal failed to open after successful health check', new Error('Modal open returned false'), {
                    category: 'modal-operation',
                    action: 'open',
                    buttonId: button?.id || 'unknown'
                });
                FallbackHandler.handle({ trigger: 'modal-open-failed' });
            }
        } catch (error) {
            this.errorHandler.handleFatalError('Unexpected error during modal opening process', error, {
                category: 'modal-operation',
                action: 'handle-opening',
                buttonId: button?.id || 'unknown'
            });
            FallbackHandler.handle({ trigger: 'modal-exception' });
        }
    }

    /**
     * Determine if modal should be reinitialized
     * @param {Object} health Health check results
     * @returns {boolean} True if reinitialize is needed
     */
    shouldReinitializeModal(health) {
        return health.components.modal && !health.modalReady;
    }

    /**
     * Attempt to reinitialize the modal
     * @param {HTMLElement} button The clicked button for context
     * @returns {boolean} True if successful
     */
    reinitializeModal(button) {
        try {
            const success = this.namespace.reinitializeModalSync();
            if (success) {
                this.logger.success('Modal reinitialized successfully');
            } else {
                this.errorHandler.handleError('Modal reinitialize returned false', new Error('Reinitialize unsuccessful'), {
                    category: 'modal-operation',
                    action: 'reinitialize',
                    buttonId: button?.id || 'unknown'
                });
            }
            return success;
        } catch (error) {
            this.errorHandler.handleError('Modal reinitialize threw exception', error, {
                category: 'modal-operation',
                action: 'reinitialize',
                buttonId: button?.id || 'unknown'
            });
            return false;
        }
    }

    /**
     * Create a fake button for manual triggering
     * @returns {HTMLElement} Fake button element
     */
    createManualTriggerButton() {
        try {
            const fakeButton = document.createElement('button');
            fakeButton.className = 'contact-us-btn';
            fakeButton.textContent = 'Manual Trigger';
            fakeButton.id = 'manual-trigger';
            return fakeButton;
        } catch (error) {
            this.errorHandler.handleError('Failed to create manual trigger button', error, {
                category: 'manual-trigger'
            });
            // Return a minimal object to prevent further errors
            return { 
                className: 'contact-us-btn', 
                textContent: 'Manual Trigger',
                id: 'manual-trigger-fallback',
                dataset: {}
            };
        }
    }

    /**
     * Process a manual contact trigger
     */
    processManualTrigger() {
        this.logger.info('Manual contact trigger initiated');
        
        try {
            const fakeButton = this.createManualTriggerButton();
            this.processClick(fakeButton);
        } catch (error) {
            this.errorHandler.handleFatalError('Manual trigger process failed', error, {
                category: 'manual-trigger'
            });
            FallbackHandler.handle({ trigger: 'manual-trigger-failed' });
        }
    }

    /**
     * Validate button element
     * @param {HTMLElement} button Button to validate
     * @returns {boolean} True if valid
     */
    validateButton(button) {
        if (!button || !(button instanceof HTMLElement)) {
            return false;
        }

        const hasValidClass = button.classList.contains('contact-us-btn') || 
                             button.hasAttribute('data-contact-trigger');
        
        if (!hasValidClass) {
            this.logger.warn('Button does not have expected contact trigger classes');
        }

        return true;
    }

    /**
     * Get click handler statistics including basic error data
     * @returns {Object} Handler statistics
     */
    getStats() {
        const errorStats = this.errorHandler.getErrorStats();
        
        return {
            hasNamespace: !!this.namespace,
            namespaceHealth: this.namespace ? this.namespace.healthCheck() : null,
            lastProcessed: this.lastProcessed || null,
            hasPendingModalConfig: !!this.pendingModalConfig,
            pendingModalType: this.pendingModalConfig?.modalType || null,
            errors: {
                total: errorStats.totalErrors,
                fatal: errorStats.fatalErrors,
                recent: errorStats.recentErrors,
                byCategory: errorStats.errorsByCategory
            }
        };
    }

    /**
     * Get recent errors for debugging
     * @param {number} minutes Number of minutes to look back
     * @returns {Array} Recent error objects
     */
    getRecentErrors(minutes = 5) {
        return this.errorHandler.getRecentErrors(minutes);
    }

    /**
     * Clear all error history
     */
    clearErrorHistory() {
        this.errorHandler.clearErrors();
    }

    /**
     * Check if there are any critical issues
     * @returns {boolean} True if fatal errors exist
     */
    hasCriticalIssues() {
        return this.errorHandler.hasFatalErrors();
    }
}